import { animated, easings, useSpring } from '@react-spring/web';
import classnames from 'classnames';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import type { Unit } from '#src/lib/style.ts';

const Wrapper = styled(animated.div)`
  position: relative;
  &:not(.open) {
    overflow: hidden;
  }
`;
const Gap = styled.div<{ $gap: Unit }>`
  display: flex;
  height: calc(var(--unit) * ${({ $gap }) => $gap});
`;

const AnimatedContent = styled(animated.div)`
  position: relative;
`;

const CollapsedContentWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

const AnimatedCollapsedContent = styled(animated.div)`
  position: relative;
`;

type Props = {
  open?: boolean;
  children: ReactNode;
  className?: string;
  gap?: Unit;
  collapsedContent?: ReactNode;
};
export const Collapse = ({
  open,
  children,
  className,
  gap = 2,
  collapsedContent,
}: Props) => {
  const [fullContentRef, { height: fullHeight }] = useMeasure<HTMLDivElement>();
  const [collapsedContentRef, { height: collapsedHeight }] =
    useMeasure<HTMLDivElement>();
  const [fullyOpened, setFullyOpened] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Used to crop the content immediatly when collapsing
  if (!open && fullyOpened) {
    setFullyOpened(false);
  }

  const getTargetHeight = () => {
    if (open) {
      return fullHeight;
    }
    if (collapsedContent != null) {
      return collapsedHeight;
    }
    return 0;
  };

  const containerStyle = useSpring({
    to: {
      height: getTargetHeight(),
    },
    config: {
      easing: open ? easings.easeOutSine : easings.easeInSine,
    },
    immediate: !mounted,
    onRest: () => {
      // Delay the "uncrop" until the animation has finished
      setFullyOpened(!!open);
    },
  });

  const childrenStyle = useSpring({
    to: {
      opacity: open ? 1 : 0,
    },
    config: {
      easing: open ? easings.easeOutSine : easings.easeInSine,
    },
    immediate: !mounted,
  });

  const collapsedContentStyle = useSpring({
    to: {
      opacity: open ? 0 : 1,
    },
    config: {
      easing: open ? easings.easeOutSine : easings.easeInSine,
    },
    immediate: !mounted,
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      setMounted(true);
    }, 300);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <Wrapper
      className={classnames(className, { open: fullyOpened })}
      style={containerStyle}
    >
      <div ref={fullContentRef}>
        {!!gap && <Gap $gap={gap} />}
        <AnimatedContent style={childrenStyle}>{children}</AnimatedContent>
      </div>
      {collapsedContent != null && (
        <CollapsedContentWrapper ref={collapsedContentRef}>
          <AnimatedCollapsedContent style={collapsedContentStyle}>
            {collapsedContent}
          </AnimatedCollapsedContent>
        </CollapsedContentWrapper>
      )}
    </Wrapper>
  );
};
